import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { UserLoginResVo } from "../http/models/UserLoginResVo";
import type { UserWalletInfoRespVo } from "../http/models/UserWalletInfoRespVo";
import type { UserPointsInfoRespVo } from "../http/models/UserPointsInfoRespVo";
import type { UserStatisticsInfoVo } from "../http/models/UserStatisticsInfoVo";
import type { BonusAccountRespVo } from "../http/models/BonusAccountRespVo";
import { loginService } from "@/services/user/auth";
import { pointService } from "@/services/points/point";
import { bonusService } from "@/services/bonus/bonus";
import i18n from "@/locales";

export const useUserStore = defineStore(
  "user",
  () => {
    // persist: true 已配置在 main.ts 中的 piniaPluginPersistedstate
    const userInfo = ref<UserLoginResVo | null>();
    const walletInfo = ref<UserWalletInfoRespVo[] | UserWalletInfoRespVo | null>();
    const walletList = computed<UserWalletInfoRespVo[]>(() => {
      if (Array.isArray(walletInfo.value)) return walletInfo.value;
      return walletInfo.value ? [walletInfo.value] : [];
    });
    const enabledWallets = computed(() =>
      walletList.value.filter(
        (wallet) => wallet.enable !== 0 && wallet.deleteStatus !== 1,
      ),
    );
    const koreaWallet = computed<UserWalletInfoRespVo | null>(
      () => enabledWallets.value.find((wallet) => wallet.walletType === 1) ?? null,
    );
    const chinaWallet = computed<UserWalletInfoRespVo | null>(
      () => enabledWallets.value.find((wallet) => wallet.walletType === 2) ?? null,
    );
    const primaryWallet = computed<UserWalletInfoRespVo | null>(() => {
      return (
        koreaWallet.value ??
        enabledWallets.value[0] ??
        null
      );
    });
    const pointsInfo = ref<UserPointsInfoRespVo | null>();
    const bonusAccountInfo = ref<BonusAccountRespVo | null>();
    const statisticsInfo = ref<UserStatisticsInfoVo | null>();
    const language = ref<string>("ko-KR");
    let loadedStatisticsUserId: number | null = null;
    let statisticsRequest: Promise<UserStatisticsInfoVo | null> | null = null;

    const setUserInfo = (info: UserLoginResVo | null) => {
      userInfo.value = info;
    };

    const setWalletInfo = (info: UserWalletInfoRespVo[] | UserWalletInfoRespVo | null) => {
      walletInfo.value = info;
    };

    const setPointsInfo = (info: UserPointsInfoRespVo | null) => {
      pointsInfo.value = info;
    };

    const setBonusAccountInfo = (info: BonusAccountRespVo | null) => {
      bonusAccountInfo.value = info;
    };

    const fetchBonusAccountInfo = async () => {
      if (userInfo.value?.showCompensationMenu !== 1) {
        bonusAccountInfo.value = null;
        return null;
      }
      const token = userInfo.value?.token;
      const data = await bonusService.getAccount();
      if (userInfo.value?.token !== token) return null;
      bonusAccountInfo.value = data;
      return data;
    };

    const fetchUserStatisticsInfo = async (force = false) => {
      const userId = userInfo.value?.id;
      if (!userId) {
        statisticsInfo.value = null;
        loadedStatisticsUserId = null;
        return null;
      }
      if (!force && loadedStatisticsUserId === userId) return statisticsInfo.value ?? null;
      if (statisticsRequest) return statisticsRequest;

      const token = userInfo.value?.token;
      const request = loginService
        .getUserStatisticsInfo(userId)
        .then((data) => {
          if (userInfo.value?.token !== token) return null;
          statisticsInfo.value = data ?? null;
          loadedStatisticsUserId = userId;
          return statisticsInfo.value;
        })
        .finally(() => {
          if (statisticsRequest === request) statisticsRequest = null;
        });
      statisticsRequest = request;
      return request;
    };

    const refreshAccountOverview = async () => {
      if (!userInfo.value?.id) return;
      const token = userInfo.value.token;

      const [walletResult, pointsResult, statisticsResult, bonusAccountResult] =
        await Promise.allSettled([
          loginService.getCurrentWallet(),
          pointService.getCurrentPoints(),
          fetchUserStatisticsInfo(true),
          fetchBonusAccountInfo(),
        ]);

      // 异步请求返回前会话可能已切换，不把旧账户的数据写入新会话。
      if (userInfo.value?.token !== token) return;

      if (walletResult.status === "fulfilled") {
        walletInfo.value = walletResult.value ?? null;
      } else {
        console.error("[UserStore] 刷新预存款信息失败:", walletResult.reason);
      }

      if (pointsResult.status === "fulfilled") {
        pointsInfo.value = pointsResult.value ?? null;
      } else {
        console.error("[UserStore] 刷新积分信息失败:", pointsResult.reason);
      }

      if (statisticsResult.status === "rejected") {
        console.error(
          "[UserStore] 刷新用户统计信息失败:",
          statisticsResult.reason,
        );
      }

      if (bonusAccountResult.status === "rejected") {
        bonusAccountInfo.value = null;
        console.error(
          "[UserStore] 刷新赔偿预存款信息失败:",
          bonusAccountResult.reason,
        );
      }
    };

    const setLanguage = (lang: string) => {
      language.value = lang;
      localStorage.setItem("language", lang);
    };
    const cleanUserInfo = () => {
      userInfo.value = null;
      walletInfo.value = null;
      pointsInfo.value = null;
      bonusAccountInfo.value = null;
      statisticsInfo.value = null;
      loadedStatisticsUserId = null;
      statisticsRequest = null;
    };

    // 不暴露凭证；读取共享存储用于识别其他窗口的登录状态。
    const readSharedUserInfo = (): UserLoginResVo | null | undefined => {
      try {
        const raw = localStorage.getItem("user");
        if (raw === null) return null;
        const saved = JSON.parse(raw);
        return saved && typeof saved === "object" ? (saved.userInfo ?? null) : undefined;
      } catch {
        // 存储不可用或内容无法解析时，不能据此清除当前登录。
        return undefined;
      }
    };

    const syncSharedUserInfo = (shared: UserLoginResVo | null) => {
      if ((userInfo.value?.token || null) === (shared?.token || null)) return;
      // 账号或会话变化后，旧窗口的账户缓存不应带入新会话。
      cleanUserInfo();
      if (shared?.token) userInfo.value = shared;
    };

    // Pinia 恢复 user 缓存后，以页面语言为准，避免旧 user.language 覆盖语言选择。
    const initFromStorage = () => {
      setLanguage(String(i18n.global.locale.value));
    };

    return {
      userInfo,
      walletInfo,
      walletList,
      koreaWallet,
      chinaWallet,
      primaryWallet,
      pointsInfo,
      bonusAccountInfo,
      statisticsInfo,
      language,
      cleanUserInfo,
      setUserInfo,
      setWalletInfo,
      setPointsInfo,
      setBonusAccountInfo,
      fetchBonusAccountInfo,
      fetchUserStatisticsInfo,
      refreshAccountOverview,
      setLanguage,
      initFromStorage,
      readSharedUserInfo,
      syncSharedUserInfo,
    };
  },
  {
    persist: true,
  },
);
